"use client";
import React, { useState } from "react";
import Button from "../../../ui/Button";
import { IconGroup } from "../../design1/components/Listing";
import { GroupHeader } from "../../../ui/Headers";
import Link from "next/link";
import { camelCaseToWords } from "../../design1/PropertyPage1";
import { TabsProps } from "../../design1/components/Listing";
import { getTypeColor } from "../../design1/components/Listing";
import { AddressIcon } from "../../design1/components/Listing";
import {
  mockProperties,
  PropertyTypeKey,
} from "../../../constants/mockProperties";
import { PropertyType } from "../../../../../types";
import CardGrid from "src/app/components/CardGrid";
import { getCurrencySign } from "src/app/lib/getCurrencySign";

import { PropertyCardSkeleton } from "../../design2/components/Skeletons/Design2PropertyCard";
import Image from "next/image";
import { generateSlugWithId } from "src/app/serverUtils";
interface Tab {
  label: React.ReactNode; // Allows JSX elements
  key: PropertyTypeKey;
  url: string;
}
const tabs: Tab[] = [
  {
    label: <>For Rent</>,
    url: "/properties/rent",
    key: "forRent",
  },
  {
    label: <>For Sale</>,
    url: "/properties/sale",
    key: "forSale",
  },
  {
    label: <> Short Let</>,
    url: "/properties/shortlet",
    key: "forShortlet",
  },
];
const Tabs: React.FC<TabsProps> = ({ tabs, activeIndex, setActiveIndex }) => {
  const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true";
  return (
    <div className="flex scrollbar-hide overflow-auto w-max mx-auto gap-4 mt-6">
      {tabs.map((tab, index) => {
        const isDisabled =
          !isDemo && (tab.key === "forSale" || tab.key === "forShortlet");
        return (
          <Button
            key={tab.key}
            onClick={() => !isDisabled && setActiveIndex(index)}
            className={`px-4 py-3 xs:px-3 xs:text-sm rounded-lg transition ${
              activeIndex === index
                ? "bg-themeColor primary-btn text-white"
                : "bg-transparent !text-themeColor border border-themeColor"
            } ${isDisabled ? "cursor-not-allowed opacity-50" : ""}`}
            disabled={isDisabled}
          >
            {tab.label}
          </Button>
        );
      })}
    </div>
  );
};
const Highlighted: React.FC<{
  variant?: boolean;
  properties: PropertyType[];
}> = ({ properties }) => {
  const isDemoMode = process.env.NEXT_PUBLIC_DEMO_MODE === "true"; // Check if DEMO_MODE is true
  const isLoading = false;

  // Ensure data is available and slice the first 3 properties
  const realProperties = properties ? [...properties.slice(-4)] : [];
  const wrappedProperties = {
    forRent: realProperties,
    forShortlet: realProperties,
    forSale: realProperties,
  };

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const activeKey: PropertyTypeKey = tabs[activeIndex].key;
  const currentProperties: PropertyType[] = isDemoMode
    ? mockProperties[activeKey] ?? []
    : wrappedProperties[activeKey] ?? [];

  const url = tabs[activeIndex].url;
  const loading = isLoading && !isDemoMode; // Check if loading and not in demo mode
  // if (isLoading && !isDemoMode) {
  //   return <div>loafing</div>;
  // }

  return (
    <section className="py-8 overflow-hidden">
      <div className="wrapper">
        <GroupHeader
          max={1024}
          subheader={
            "Premium properties that distinguish themselves through their quality, location, and value."
          }
          header={"Highlighted Properties"}
        />

        {/* Tabs Component */}
        <Tabs
          tabs={tabs}
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
        />

        {/* Tab Content */}
        <div className="mt-6 py-4 ">
          {loading ? (
            <CardGrid minCardWidth={250} maxColumns={4}>
              {Array.from({ length: 4 }, (_, index) => (
                <PropertyCardSkeleton key={index} />
              ))}
            </CardGrid>
          ) : currentProperties.length > 0 ? (
            <CardGrid minCardWidth={300} maxColumns={4}>
              {currentProperties.map((property) => (
                <Design3Card
                  className="!w-full"
                  key={property.id}
                  {...property}
                />
              ))}
            </CardGrid>
          ) : (
            <p className="text-center text-gray-500">
              No properties available.
            </p>
          )}
          {}

          {currentProperties.length > 0 && (
            <Button
              href={url}
              className="bg-themeColor primary-btn !px-8 !font-normal text-[14px]  py-3 block mt-16 !text-white w-max mx-auto"
            >
              Discover More Listing
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};
export default Highlighted;

interface ExtendedProperty extends PropertyType {
  className?: string;
}

export const Design3Card: React.FC<ExtendedProperty> = ({
  images,
  title,
  address,
  reviews,
  size,
  id,
  price,
  beforePrice,
  currency,
  strike,
  type,
  quote,
  quotePrice,
  priceType,
  category,
  className,
}) => {
  const slugwithid = generateSlugWithId(Number(id), title);
  const baseUrl = `/properties/${
    type === "short let" ? "shortlet" : type
  }/${slugwithid}`;

  const formattedCurrency = getCurrencySign(currency);
  return (
    <Link
      href={baseUrl}
      style={{
        boxShadow: "0px 2px 25px 0px rgba(33, 33, 33, 0.08)",
      }}
      className={`flex flex-col transition-all duration-500 hover:scale-105 p-1 h-full sm:mx-auto w-[340px] xs:w-full  text-primary bg-white border overflow-hidden rounded-2xl ${className}`}
    >
      <div className="first rounded-xl overflow-hidden group relative w-full">
        <div className="hover group-hover:flex hidden transition-all duration-500 absolute bg-black/60 items-center justify-center z-10 top-0 left-0 w-full h-full">
          <Button className="bg-white !text-themeColor w-max h-max">
            View Details
          </Button>
        </div>
        <div
          className="bottom text-white absolute flex 
        gap-2 bottom-2 right-2"
        >
          <span className=" flex gap-1 bg-[#2F364B99]/60 px-2 rounded-md items-center  p-1 text-sm">
            {images.length} <IconFan />
          </span>
        </div>

        <div className="commer bg-white p-1 px-2 rounded-md absolute text-themeColor bottom-2 left-2">
          <IconGroup className="!text-themeColor text-sm">{category}</IconGroup>
        </div>
        <div
          className={`tag absolute capitalize text-[11px] p-1 px-2 rounded-md text-center left-2 top-2 transform origin-center ${getTypeColor(
            type
          )}`}
        >
          <span>{type !== "short let" && "For"}</span> {type}
        </div>
        <Image
          height={300}
          width={300}
          src={images[0]}
          alt={title}
          className="w-full   h-[200px] object-cover rounded-l-md"
        />
      </div>
      <div className="second w-full p-4">
        <div className="first text-sm mb-2">
          <h4 className=" !font-bold h6 truncate-2-lines">{title}</h4>
          <IconGroup className="!text-sm mt-1 ">
            <span className="block">
              <AddressIcon />
            </span>
            <span className="truncate">{address}</span>
          </IconGroup>
        </div>
        <div className="flex items-center gap-8">
          <div className="item -ml-[2px] flex gap-6">
            <IconGroup>
              <SizeIcon /> <span>{size!.toLocaleString()} sqm</span>
            </IconGroup>
          </div>
          <IconGroup>
            <ReviewIcon /> {reviews.length} reviews
          </IconGroup>
        </div>

        <div className="last flex items-center gap-2  border-t pt-3 border-t-disabled2-main mt-3">
          <div className="">
            <h3 className="font-semibold leading-6 items-end gap-1 flex text-themeColor ">
              {formattedCurrency}
              {price.toLocaleString()}
              {beforePrice && strike ? (
                <span className="text-[13px]  leading-none line-through whitespace-nowrap text-secondary-main">
                  {beforePrice.toLocaleString()}
                </span>
              ) : (
                <span className="text-[13px] leading-none whitespace-nowrap text-secondary-main">
                  {camelCaseToWords(priceType)}
                </span>
              )}
            </h3>
            <span className="text-[13px] capitalize text-secondary-main">
              <span className="text-themeColor">
                {" "}
                <span className="text-themeColor text-[14px]">
                  {typeof quotePrice === "number" ? (
                    <span>
                      {" "}
                      {formattedCurrency}
                      {quotePrice.toLocaleString()}{" "}
                    </span>
                  ) : (
                    <span>{quotePrice}</span>
                  )}
                </span>
              </span>
              / {camelCaseToWords(quote)}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export const IconFan = () => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.5 7.5H12.0306C12.371 6.81396 12.5306 6.05241 12.4945 5.28743C12.4583 4.52245 12.2275 3.77936 11.8239 3.12851C11.4203 2.47765 10.8573 1.94056 10.1881 1.56809C9.51897 1.19562 8.76583 1.00009 8 1C7.86739 1 7.74021 1.05268 7.64645 1.14645C7.55268 1.24021 7.5 1.36739 7.5 1.5V3.96937C6.81396 3.629 6.05241 3.46935 5.28743 3.50553C4.52245 3.5417 3.77936 3.77251 3.12851 4.17611C2.47765 4.5797 1.94056 5.14272 1.56809 5.81187C1.19562 6.48103 1.00009 7.23417 1 8C1 8.13261 1.05268 8.25979 1.14645 8.35355C1.24021 8.44732 1.36739 8.5 1.5 8.5H3.96937C3.629 9.18604 3.46935 9.94759 3.50553 10.7126C3.5417 11.4775 3.77251 12.2206 4.17611 12.8715C4.5797 13.5224 5.14272 14.0594 5.81187 14.4319C6.48103 14.8044 7.23417 14.9999 8 15C8.13261 15 8.25979 14.9473 8.35355 14.8536C8.44732 14.7598 8.5 14.6326 8.5 14.5V12.0306C9.18604 12.371 9.94759 12.5306 10.7126 12.4945C11.4775 12.4583 12.2206 12.2275 12.8715 11.8239C13.5224 11.4203 14.0594 10.8573 14.4319 10.1881C14.8044 9.51897 14.9999 8.76583 15 8C15 7.86739 14.9473 7.74021 14.8536 7.64645C14.7598 7.55268 14.6326 7.5 14.5 7.5ZM5.5 4.5C6.21528 4.49636 6.91407 4.71473 7.5 5.125V7.5H2.03562C2.15697 6.66744 2.5737 5.9063 3.20973 5.35553C3.84575 4.80476 4.65865 4.5011 5.5 4.5ZM10.5 11.5C9.78472 11.5036 9.08593 11.2853 8.5 10.875V8.5H13.9644C13.843 9.33256 13.4263 10.0937 12.7903 10.6445C12.1542 11.1952 11.3414 11.4989 10.5 11.5Z"
        fill="white"
      />
    </svg>
  );
};

export const ReviewIcon = () => {
  return (
    <svg
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.332 1.83203H2.66536C1.93203 1.83203 1.3387 2.43203 1.3387 3.16536L1.33203 15.1654L3.9987 12.4987H13.332C14.0654 12.4987 14.6654 11.8987 14.6654 11.1654V3.16536C14.6654 2.43203 14.0654 1.83203 13.332 1.83203ZM13.332 11.1654H3.44536L3.05203 11.5587L2.66536 11.9454V3.16536H13.332V11.1654ZM6.9987 9.83203H11.9987V8.4987H8.33203L6.9987 9.83203ZM9.57203 5.9187C9.70536 5.78536 9.70536 5.5787 9.57203 5.44536L8.39203 4.26536C8.2587 4.13203 8.05203 4.13203 7.9187 4.26536L3.9987 8.18536V9.83203H5.64536L9.57203 5.9187Z"
        fill="var(--primary-color)"
      />
    </svg>
  );
};

export const SizeIcon = () => {
  return (
    <svg
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.46484 11.7526H8.93151C9.69818 11.7526 10.3248 11.1259 10.3248 10.3592V9.89258H8.46484V11.7526Z"
        fill="var(--primary-color)"
      />
      <path
        d="M5.67188 10.3612C5.67188 11.1279 6.29854 11.7545 7.06521 11.7545H7.53187V9.89453H5.67188V10.3612Z"
        fill="var(--primary-color)"
      />
      <path
        d="M5.67188 8.4988V8.96547H7.53187V7.10547H7.06521C6.29854 7.10547 5.67188 7.73214 5.67188 8.4988Z"
        fill="var(--primary-color)"
      />
      <path
        d="M13.352 5.04776L9.5187 2.3611C8.47203 1.62776 6.87203 1.66776 5.86536 2.44776L2.52536 5.05443C1.85203 5.57443 1.33203 6.6411 1.33203 7.4811V12.0811C1.33203 13.7811 2.71203 15.1678 4.40536 15.1678H11.5854C13.2787 15.1678 14.6587 13.7878 14.6587 12.0878V7.56776C14.6654 6.66776 14.0854 5.5611 13.352 5.04776ZM11.252 10.3611C11.252 11.6411 10.2054 12.6878 8.92536 12.6878H7.06536C5.78536 12.6878 4.7387 11.6478 4.7387 10.3611V8.5011C4.7387 7.2211 5.78536 6.17443 7.06536 6.17443H8.92536C10.2054 6.17443 11.252 7.21443 11.252 8.5011V10.3611Z"
        fill="var(--primary-color)"
      />
      <path
        d="M8.93151 7.10742H8.46484V8.96742H10.3248V8.50076C10.3248 7.73409 9.69818 7.10742 8.93151 7.10742Z"
        fill="var(--primary-color)"
      />
    </svg>
  );
};
